import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileSignature, Plus, Loader2, BellRing, PenLine, Download, Trash2, Copy, CheckCircle2, X, CalendarClock, AlertTriangle, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, downloadAuthedFile } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

const STATUS_CLS = {
  sent: "text-zinc-400 border-white/20",
  viewed: "text-nc-blue border-nc-blue/40 bg-nc-blue/5",
  signed: "text-nc-orange border-nc-orange/40 bg-nc-orange/5",
  completed: "text-nc-green border-nc-green/40 bg-nc-green/5",
};

function ContractDateField({ label, value, onChange, testId, placeholder = "Pick a date" }) {
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  const setDate = (d) => {
    if (!d) return onChange("");
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    onChange(iso);
  };
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId} className={`${inputCls} flex items-center justify-between text-left`}>
            <span className={value ? "text-zinc-100" : "text-zinc-600"}>
              {value ? new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(selected) : placeholder}
            </span>
            <CalendarDays className="w-3.5 h-3.5 text-zinc-600" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto border-white/10 bg-[#080808] p-0 text-zinc-100">
          <Calendar mode="single" selected={selected} onSelect={setDate} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}


export default function AdminContracts() {
  const [contracts, setContracts] = useState([]);
  const [renewals, setRenewals] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signingId, setSigningId] = useState(null);
  const [signName, setSignName] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [form, setForm] = useState({
    party_name: "", party_email: "", contract_type: "services", project: "New Citizen", role_title: "Game Developer",
    scope: "", fee_terms: "", start_date: "", end_date: "",
  });

  const [loadError, setLoadError] = useState("");
  const [diag, setDiag] = useState(null);

  const load = () => {
    setLoadError("");
    api.get("/admin/contracts").then((r) => { setContracts(r.data.contracts); setDiag(r.data.diag || null); }).catch((err) => {
      setLoadError(`Contracts register failed to load: ${err.response?.status || "network"} ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
    });
    api.get("/admin/contracts/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/admin/contracts/renewals?days=90").then((r) => setRenewals(r.data.alerts || [])).catch(() => setRenewals([]));
  };
  useEffect(() => { load(); }, []);

  const isNda = form.contract_type === "nda";
  const formValid = form.party_name.trim().length >= 2 && form.party_email.includes("@")
    && (isNda || (form.scope.trim().length >= 20 && form.fee_terms.trim().length >= 4));

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/contracts", form);
      toast.success(`Contract sent to ${form.party_email}`);
      setShowForm(false);
      setForm({ party_name: "", party_email: "", contract_type: "services", project: "New Citizen", role_title: "Game Developer", scope: "", fee_terms: "", start_date: "", end_date: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const act = async (fn, ok) => {
    try { const r = await fn(); toast.success(ok || r.data.message); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const countersign = async (cid) => {
    if (signName.trim().length < 2) { toast.error("Type the studio signatory name"); return; }
    await act(() => api.post(`/admin/contracts/${cid}/countersign`, { signer_name: signName.trim() }));
    setSigningId(null); setSignName("");
  };

  return (
    <section data-testid="admin-contracts">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase">E-Sign Contracts</p>
          <p className="font-mono text-xs text-zinc-500 mt-1">Send legally binding development contracts to any dev or freelancer — they sign online, you countersign, everyone gets the PDF.</p>
        </div>
        <button data-testid="contract-new" onClick={() => setShowForm((s) => !s)} className="font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue hover:bg-nc-blue/80 text-white px-4 py-2.5 transition-colors flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Contract
        </button>
      </div>

      {renewals !== null && (
        <div data-testid="contract-renewal-radar" className={`border p-4 mb-6 ${renewals.length ? "border-nc-orange/40 bg-nc-orange/5" : "border-nc-green/25 bg-nc-green/5"}`}>
          <div className="flex items-center gap-2 mb-3">
            {renewals.length ? <AlertTriangle className="w-4 h-4 text-nc-orange" /> : <CalendarClock className="w-4 h-4 text-nc-green" />}
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300">
              Renewal radar {renewals.length ? `— ${renewals.length} contract${renewals.length === 1 ? "" : "s"} need attention` : "— nothing due in the next 90 days"}
            </p>
          </div>
          {renewals.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
              {renewals.slice(0, 8).map((r) => (
                <div key={`${r.source}-${r.id}`} data-testid={`renewal-alert-${r.id}`} className="border border-white/10 bg-black/35 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-sans font-semibold text-xs text-zinc-100 truncate">{r.party_name}</p>
                    <span className={`font-mono text-[8px] uppercase tracking-[0.15em] ${r.urgency === "overdue" || r.urgency === "critical" ? "text-red-400" : r.urgency === "soon" ? "text-nc-orange" : "text-nc-blue"}`}>
                      {r.days_left < 0 ? `${-r.days_left}d overdue` : `${r.days_left}d left`}
                    </span>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-500 mt-1 truncate">{r.project} · {r.term_label}</p>
                  <p className="font-mono text-[9px] text-zinc-400 mt-2">Ends {r.end_date}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      <AnimatePresence>
        {showForm && (
          <motion.div data-testid="contract-form" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="border border-white/10 bg-[#070707] p-6 mb-6 overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
                <div className="flex gap-1.5">
                  {[["services", "Services Contract"], ["nda", "Mutual NDA"]].map(([t, label]) => (
                    <button key={t} data-testid={`contract-type-${t}`} onClick={() => setForm({ ...form, contract_type: t })} className={`font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors ${form.contract_type === t ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/15 text-zinc-500"}`}>{label}</button>
                  ))}
                </div>
                {templates.length > 0 && (
                  <select data-testid="contract-template-load" defaultValue="" onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) { setForm((f) => ({ ...f, contract_type: t.contract_type, project: t.project, role_title: t.role_title, scope: t.scope, fee_terms: t.fee_terms })); toast.success(`Template "${t.name}" loaded`); } }} className="bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 focus:outline-none">
                    <option value="">Load template…</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
              </div>
              <div><span className={labelCls}>Contractor full name *</span><input data-testid="contract-party-name" className={inputCls} value={form.party_name} maxLength={80} onChange={(e) => setForm({ ...form, party_name: e.target.value })} /></div>
              <div><span className={labelCls}>Contractor email *</span><input data-testid="contract-party-email" type="email" className={inputCls} value={form.party_email} maxLength={160} onChange={(e) => setForm({ ...form, party_email: e.target.value })} /></div>
              <div><span className={labelCls}>Project</span><input data-testid="contract-project" className={inputCls} value={form.project} maxLength={80} onChange={(e) => setForm({ ...form, project: e.target.value })} /></div>
              {!isNda && <div><span className={labelCls}>Role title</span><input data-testid="contract-role" className={inputCls} value={form.role_title} maxLength={80} onChange={(e) => setForm({ ...form, role_title: e.target.value })} /></div>}
              <ContractDateField label="Start date" testId="contract-start-date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
              <ContractDateField label={isNda ? "NDA end date — optional override" : "Engagement end date — optional"} testId="contract-end-date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} />
              {!isNda && <div><span className={labelCls}>Fee terms *</span><input data-testid="contract-fee" className={inputCls} placeholder="e.g. AUD 40/hr, invoiced weekly, 14-day terms" value={form.fee_terms} maxLength={1000} onChange={(e) => setForm({ ...form, fee_terms: e.target.value })} /></div>}
              {!isNda && (
                <div className="sm:col-span-2"><span className={labelCls}>Scope of work * — what they're building</span><textarea data-testid="contract-scope" rows={4} className={`${inputCls} resize-none`} value={form.scope} maxLength={4000} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="e.g. Vehicle systems programming for New Citizen: FGear integration, ELS lightbar sync, weekly milestones as agreed in Discord." /></div>
              )}
              {isNda && <p className="sm:col-span-2 font-mono text-[9px] text-zinc-500 leading-relaxed border border-white/10 p-3">Mutual NDA covers pre-hire conversations: designs, builds, code, business terms — 2-year term, confidentiality survives 5 years. Standard body, no scope needed.</p>}
            </div>
            <div className="flex gap-3 mt-4 flex-wrap items-center">
              <button data-testid="contract-send" disabled={busy || !formValid} onClick={create} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Send for Signature
              </button>
              <div className="flex items-center gap-2">
                <input data-testid="template-name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} maxLength={60} placeholder="Template name…" className="bg-[#050505] border border-white/10 px-2.5 py-2 font-mono text-[10px] text-zinc-100 w-40 focus:outline-none" />
                <button data-testid="template-save" onClick={async () => { if (templateName.trim().length < 2) { toast.error("Name the template"); return; } try { await api.post("/admin/contracts/templates", { name: templateName.trim(), contract_type: form.contract_type, project: form.project, role_title: form.role_title, scope: form.scope, fee_terms: form.fee_terms }); toast.success("Template saved"); setTemplateName(""); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }} className="font-mono text-[9px] uppercase border border-white/15 text-zinc-400 hover:text-zinc-100 px-3 py-2 transition-colors">Save as template</button>
              </div>
              <button onClick={() => setShowForm(false)} className="font-mono text-[10px] uppercase text-zinc-500 hover:text-zinc-200 px-3">Cancel</button>
            </div>
            <p className="font-mono text-[8px] text-zinc-600 mt-3">Unsigned contracts get automatic email reminders every 48h (max 3) — plus a manual nudge button below each contract.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {loadError && (
        <div data-testid="contracts-load-error" className="border border-red-500/40 bg-red-500/5 p-4 mb-4 flex items-center justify-between gap-3">
          <p className="font-mono text-[10px] text-red-300">{loadError}</p>
          <button data-testid="contracts-retry" onClick={load} className="font-mono text-[9px] uppercase border border-red-500/40 text-red-300 px-3 py-2 hover:bg-red-500/10 transition-colors shrink-0">Retry</button>
        </div>
      )}

      {contracts.length === 0 && !loadError ? (
        <div className="border border-dashed border-white/15 p-10 text-center font-mono text-xs text-zinc-600">No contracts sent yet.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {contracts.map((c, i) => (
            <div key={c.id} data-testid={`contract-row-${i}`} className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <FileSignature className="w-4 h-4 text-zinc-500" />
                <p className="font-sans font-bold text-sm text-zinc-50">{c.party_name}</p>
                <span className="font-mono text-[9px] text-zinc-500">{c.party_email}</span>
                <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-1 ${STATUS_CLS[c.status] || STATUS_CLS.sent}`}>{c.status}</span>
                {c.contract_type === "nda" && <span className="font-mono text-[8px] tracking-[0.2em] uppercase border border-white/25 text-zinc-300 px-2 py-1">NDA</span>}
                {c.end_date && <span data-testid={`contract-end-${i}`} className="font-mono text-[8px] tracking-[0.2em] uppercase border border-nc-blue/30 text-nc-blue px-2 py-1">Ends {String(c.end_date).slice(0, 10)}</span>}
                {c.legacy && <span className="font-mono text-[8px] tracking-[0.2em] uppercase border border-white/15 text-zinc-500 px-2 py-1">Legacy DevHub</span>}
                <span className="font-mono text-[8px] text-zinc-600 ml-auto">{c.project} · {c.role_title} · {String(c.created_at).slice(0, 10)}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {!c.legacy && ["sent", "viewed"].includes(c.status) && (
                  <button data-testid={`contract-remind-${i}`} onClick={() => act(() => api.post(`/admin/contracts/${c.id}/remind`))} className="font-mono text-[8px] uppercase tracking-[0.15em] border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-3 py-2 transition-colors flex items-center gap-1.5">
                    <BellRing className="w-3 h-3" /> Remind ({c.reminders_sent || 0} sent)
                  </button>
                )}
                {!c.legacy && c.status === "signed" && (
                  signingId === c.id ? (
                    <div className="flex items-center gap-2">
                      <input data-testid={`contract-sign-name-${i}`} value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Studio signatory name" className="bg-[#050505] border border-nc-green/40 px-2.5 py-2 font-sans italic text-sm text-zinc-100 focus:outline-none" />
                      <button data-testid={`contract-countersign-${i}`} onClick={() => countersign(c.id)} className="font-mono text-[8px] uppercase bg-nc-green text-black px-3 py-2.5 hover:bg-nc-green/85 transition-colors flex items-center gap-1"><PenLine className="w-3 h-3" /> Countersign</button>
                      <button onClick={() => setSigningId(null)} className="text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button data-testid={`contract-countersign-open-${i}`} onClick={() => setSigningId(c.id)} className="font-mono text-[8px] uppercase tracking-[0.15em] bg-nc-green text-black hover:bg-nc-green/85 px-3 py-2 transition-colors flex items-center gap-1.5">
                      <PenLine className="w-3 h-3" /> Countersign Now
                    </button>
                  )
                )}
                {c.status === "completed" && <span className="font-mono text-[8px] text-nc-green flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Fully executed — PDF in contractor's inbox</span>}
                {c.legacy ? (
                  <button data-testid={`contract-pdf-${i}`} onClick={() => act(() => downloadAuthedFile(`/contracts/${c.legacy_doc_id}/pdf`, `specpixel-legacy-${c.id.slice(0, 6)}.pdf`), "PDF downloaded")} className="font-mono text-[8px] uppercase tracking-[0.15em] border border-white/15 text-zinc-400 hover:text-zinc-100 px-3 py-2 transition-colors flex items-center gap-1.5"><Download className="w-3 h-3" /> PDF</button>
                ) : (
                  <a data-testid={`contract-pdf-${i}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/sign/${c.token}/pdf`} className="font-mono text-[8px] uppercase tracking-[0.15em] border border-white/15 text-zinc-400 hover:text-zinc-100 px-3 py-2 transition-colors flex items-center gap-1.5"><Download className="w-3 h-3" /> PDF</a>
                )}
                {!c.legacy && <button data-testid={`contract-delete-${i}`} onClick={() => act(() => api.delete(`/admin/contracts/${c.id}`), "Deleted")} className="font-mono text-[8px] uppercase border border-white/15 text-zinc-600 hover:text-red-400 px-2 py-2 transition-colors"><Trash2 className="w-3 h-3" /></button>}
              </div>
              {(c.events || []).length > 0 && (
                <p className="font-mono text-[8px] text-zinc-600">{(c.events || []).map((e) => e.kind).join(" → ")} · {(c.events || []).length} events logged</p>
              )}
            </div>
          ))}
        </div>
      )}
      {diag && (
        <p data-testid="contracts-diag" className="font-mono text-[8px] text-zinc-600 mt-6 uppercase tracking-[0.15em]">
          Register sources: {diag.esign} e-sign contracts · {diag.legacy_issued} legacy issued · {diag.legacy_signed} legacy signed — {contracts.length} shown
        </p>
      )}

      {/* blank template library (from the retired DevHub system) */}
      <div className="border border-white/10 bg-[#050505]/60 p-5 mt-8">
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase mb-1.5">Blank Template Library</p>
        <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-relaxed">Formal blank copies of the standard agreements as watermarked PDFs — for reference or offline signing.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[["nda", "Non-Disclosure Agreement"], ["ip", "IP Assignment Agreement"], ["contract", "Software Development Agreement"]].map(([t, label]) => (
            <div key={t} data-testid={`template-card-${t}`} className="border border-white/10 bg-black/40 p-4 flex flex-col gap-3">
              <p className="font-sans font-semibold text-sm text-zinc-100">{label}</p>
              <button data-testid={`template-pdf-${t}`} onClick={() => act(() => downloadAuthedFile(`/contracts/template/${t}/pdf`, `specpixel-${t}-template.pdf`), "Template downloaded")} className="self-start font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Download PDF
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
